import {
  UHF_API_BASE,
  UHF_PARTNER_ID,
  UHF_FOOTER_ID,
  UHF_REQUEST_TIMEOUT,
  getCacheKey,
  getUHFLocale,
} from "./uhfConfig";

export interface UHFFooterData {
  footerHtml: string;
  cssHtml: string;
  jsHtml: string;
}

/**
 * Parse UHF XML response.
 * XML CDATA sections contain raw HTML for footer, CSS, and JS.
 */
function parseUHFXml (xmlText: string): UHFFooterData | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, "text/xml");

    const footerHtml = doc.querySelector("footerHtml")?.textContent || "";
    const cssHtml = doc.querySelector("cssIncludes")?.textContent || "";
    const jsHtml = doc.querySelector("javascriptIncludes")?.textContent || "";

    if (!footerHtml) return null;
    return { footerHtml, cssHtml, jsHtml };
  } catch {
    return null;
  }
}

/**
 * Fetch UHF footer data from the Shell Service XML API.
 * URL format: /uhf-api/{locale}/shell/xml/{partnerId}?FooterId={footerId}
 * Uses sessionStorage caching to avoid redundant requests.
 *
 * @param appLocale - App locale code (e.g. "zh", "en")
 * @returns Parsed UHF footer data or null on failure
 */
export async function fetchUHFFooter (appLocale: string): Promise<UHFFooterData | null> {
  if (!UHF_PARTNER_ID) {
    console.warn("UHF_PARTNER_ID is not configured. Set the UHF_PARTNER_ID environment variable.");
    return null;
  }

  const uhfLocale = getUHFLocale(appLocale);
  const cacheKey = getCacheKey(uhfLocale);

  // Check sessionStorage cache first
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached) as UHFFooterData;
    }
  } catch {
    // sessionStorage may be unavailable; continue without cache
  }

  // Fetch from UHF Shell Service XML API
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), UHF_REQUEST_TIMEOUT);

  try {
    const footerParam = UHF_FOOTER_ID ? `&FooterId=${encodeURIComponent(UHF_FOOTER_ID)}` : "";
    const url = `${UHF_API_BASE}/${uhfLocale}/shell/xml/${encodeURIComponent(UHF_PARTNER_ID)}?${footerParam}`;
    console.debug(`[UHF] Fetching footer: ${url}`);
    const response = await fetch(url, {
      signal: controller.signal,
    });

    if (!response.ok) {
      console.warn(`UHF footer API returned ${response.status}`);
      return null;
    }

    const xmlText = await response.text();
    const result = parseUHFXml(xmlText);

    if (!result) {
      console.warn("UHF footer API returned empty or unparseable response");
      return null;
    }

    // Cache successful response
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(result));
    } catch {
      // sessionStorage quota exceeded or unavailable; non-critical
    }

    return result;
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      console.warn("UHF footer API request timed out");
    } else {
      console.warn("UHF footer API request failed:", err);
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
